<template>
  <v-card outlined class="bid-sage-panel" :class="{ collapsed: collapsed }">
    <div class="bid-sage-header px-3 py-2 d-flex align-center">
      <v-icon color="white" small class="mr-2">mdi-robot-happy</v-icon>
      <div class="flex-grow-1">
        <div class="text-subtitle-2 font-weight-bold white--text mb-0">
          Sage on this bid
        </div>
        <span class="text-caption" style="color: rgba(255, 255, 255, 0.7)">
          Refine requirements, find swaps, re-rank
        </span>
      </div>
      <v-btn icon x-small dark @click="collapsed = !collapsed">
        <v-icon small>{{ collapsed ? "mdi-chevron-down" : "mdi-chevron-up" }}</v-icon>
      </v-btn>
    </div>

    <v-expand-transition>
      <div v-show="!collapsed">
        <div class="bid-sage-messages" ref="messageContainer">
          <div v-if="messages.length === 0" class="pa-4 text-center">
            <v-avatar size="48" color="primary" class="mb-2">
              <v-icon dark>mdi-robot-happy</v-icon>
            </v-avatar>
            <p class="text-body-2 grey--text text--darken-1 mb-3">
              Ask Sage to refine the requirements, suggest swaps for a weak role,
              or re-rank how the report reads. Sage suggests; you decide.
            </p>
            <div class="d-flex flex-column" style="gap: 6px">
              <v-chip
                v-for="(chip, cIdx) in starterChips"
                :key="cIdx"
                small
                outlined
                color="primary"
                class="starter-chip"
                @click="sendQuick(chip)"
              >
                {{ chip }}
              </v-chip>
            </div>
          </div>

          <div v-else class="pa-3">
            <div
              v-for="(msg, idx) in messages"
              :key="idx"
              :class="['bsp-row', msg.role === 'user' ? 'user-row' : 'assistant-row']"
            >
              <div v-if="msg.role === 'user'" class="d-flex justify-end">
                <div class="bsp-bubble user-bubble">
                  <div class="text-body-2 white--text">{{ msg.content }}</div>
                </div>
              </div>

              <div v-else class="d-flex align-start">
                <v-avatar size="26" color="primary" class="mr-2 mt-1 flex-shrink-0">
                  <v-icon dark x-small>mdi-robot-happy</v-icon>
                </v-avatar>
                <div class="bsp-bubble assistant-bubble flex-grow-1">
                  <div class="text-body-2" v-html="formatMessage(msg.content)"></div>

                  <!-- Bid tool results -->
                  <div
                    v-if="msg.toolResults && msg.toolResults.length > 0"
                    class="mt-2"
                  >
                    <div v-for="(tr, trIdx) in msg.toolResults" :key="trIdx">
                      <!-- refine_requirements: accept/reject each proposed edit -->
                      <div v-if="tr.tool === 'refine_requirements'" class="tool-block">
                        <div class="text-caption font-weight-bold mb-2 theme-subheading">
                          Proposed requirement changes
                        </div>
                        <div
                          v-for="(edit, eIdx) in proposedEdits(tr)"
                          :key="'edit-' + eIdx"
                          class="edit-row pa-2 mb-2"
                        >
                          <div class="d-flex align-center mb-1">
                            <v-chip
                              x-small
                              :color="editActionColor(edit.action)"
                              text-color="white"
                              class="mr-2"
                            >
                              {{ editActionLabel(edit.action) }}
                            </v-chip>
                            <span class="text-caption font-weight-bold">
                              {{ edit.roleTitle || roleTitleFor(edit.roleId) }}
                            </span>
                          </div>
                          <div class="text-body-2 mb-1">
                            "{{ edit.requirementText }}"
                            <span
                              v-if="edit.action === 'reweight' && edit.newWeight != null"
                              class="text-caption grey--text"
                            >
                              → weight {{ edit.newWeight }}%
                            </span>
                            <span
                              v-if="edit.action === 'reimportance' && edit.newImportance"
                              class="text-caption grey--text"
                            >
                              → {{ importanceLabel(edit.newImportance) }}
                            </span>
                          </div>
                          <div v-if="edit.reason" class="text-caption grey--text mb-2" style="font-style: italic">
                            {{ edit.reason }}
                          </div>
                          <div v-if="!isEditResolved(idx, trIdx, eIdx)" class="d-flex" style="gap: 6px">
                            <v-btn
                              x-small
                              color="#10B981"
                              dark
                              @click="acceptEdit(idx, trIdx, eIdx, edit)"
                            >
                              <v-icon x-small left>mdi-check</v-icon>
                              Accept
                            </v-btn>
                            <v-btn
                              x-small
                              outlined
                              color="grey darken-1"
                              @click="rejectEdit(idx, trIdx, eIdx)"
                            >
                              Reject
                            </v-btn>
                          </div>
                          <div
                            v-else
                            class="text-caption font-weight-bold"
                            :style="{ color: editResolution(idx, trIdx, eIdx) === 'accepted' ? '#10B981' : '#9E9E9E' }"
                          >
                            <v-icon
                              x-small
                              :color="editResolution(idx, trIdx, eIdx) === 'accepted' ? '#10B981' : '#9E9E9E'"
                            >
                              {{ editResolution(idx, trIdx, eIdx) === 'accepted' ? 'mdi-check-circle' : 'mdi-close-circle' }}
                            </v-icon>
                            {{ editResolution(idx, trIdx, eIdx) === 'accepted' ? 'Applied' : 'Rejected' }}
                          </div>
                        </div>
                        <v-btn
                          v-if="anyEditAccepted(idx, trIdx, tr)"
                          x-small
                          color="#10B981"
                          dark
                          class="mt-1"
                          :loading="isAuditing"
                          @click="$emit('rerun')"
                        >
                          <v-icon x-small left>mdi-refresh</v-icon>
                          Re-run audit
                        </v-btn>
                      </div>

                      <!-- suggest_swaps: candidate cards, add to a role -->
                      <div v-else-if="tr.tool === 'suggest_swaps'" class="tool-block">
                        <div class="text-caption font-weight-bold mb-2 theme-subheading">
                          Swap candidates
                        </div>
                        <v-card
                          v-for="(cand, candIdx) in swapResults(tr)"
                          :key="'cand-' + candIdx"
                          outlined
                          class="mb-2"
                        >
                          <v-card-text class="pa-2">
                            <div class="d-flex align-center justify-space-between">
                              <div>
                                <div class="font-weight-bold text-body-2">{{ cand.name }}</div>
                                <div class="text-caption grey--text">
                                  {{ cand.title }}
                                  <span v-if="cand.company"> at {{ cand.company }}</span>
                                </div>
                              </div>
                              <v-chip
                                v-if="cand.score != null"
                                x-small
                                :color="scoreColor(cand.score)"
                                dark
                              >
                                {{ cand.score }}%
                              </v-chip>
                            </div>
                            <div v-if="cand.key_skills && cand.key_skills.length" class="mt-1">
                              <v-chip
                                v-for="skill in cand.key_skills.slice(0, 4)"
                                :key="skill"
                                x-small
                                outlined
                                class="mr-1 mt-1"
                              >
                                {{ skill }}
                              </v-chip>
                            </div>
                            <div
                              v-if="cand.strongest_signal"
                              class="text-caption font-italic mt-1"
                              style="color: #6b7280; line-height: 1.3"
                            >
                              {{ cand.strongest_signal }}
                            </div>
                            <div class="mt-2 d-flex align-center" style="gap: 6px">
                              <v-select
                                :items="roleSelectItems"
                                :value="swapRoleChoice(idx, trIdx, candIdx, tr)"
                                dense
                                outlined
                                hide-details
                                label="Role"
                                style="max-width: 180px"
                                @change="setSwapRoleChoice(idx, trIdx, candIdx, $event)"
                              />
                              <v-btn
                                x-small
                                color="#10B981"
                                dark
                                @click="addSwapCandidate(cand, swapRoleChoice(idx, trIdx, candIdx, tr))"
                              >
                                <v-icon x-small left>mdi-plus</v-icon>
                                Add to team
                              </v-btn>
                            </div>
                          </v-card-text>
                        </v-card>
                      </div>

                      <!-- re_rank_audit: explanation + apply view (display-only) -->
                      <div v-else-if="tr.tool === 're_rank_audit'" class="tool-block">
                        <div class="text-caption font-weight-bold mb-2 theme-subheading">
                          Suggested view
                        </div>
                        <div class="rerank-card pa-2">
                          <div class="text-body-2 mb-2">
                            {{ proposedView(tr).explanation }}
                          </div>
                          <div class="text-caption grey--text mb-2" style="font-style: italic">
                            View only — this changes ordering and filtering, never any score.
                          </div>
                          <v-btn
                            x-small
                            color="#8b5cf6"
                            dark
                            @click="$emit('apply-view', proposedView(tr))"
                          >
                            <v-icon x-small left>mdi-eye-outline</v-icon>
                            Apply view
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="isLoading" class="bsp-row assistant-row">
              <div class="d-flex align-start">
                <v-avatar size="26" color="primary" class="mr-2 mt-1 flex-shrink-0">
                  <v-icon dark x-small>mdi-robot-happy</v-icon>
                </v-avatar>
                <div class="bsp-bubble assistant-bubble">
                  <div class="typing-indicator">
                    <span></span><span></span><span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- suggestedFollowUps as clickable chips -->
          <div
            v-if="followUps.length > 0 && !isLoading"
            class="px-3 pb-2"
          >
            <div class="d-flex flex-wrap" style="gap: 6px">
              <v-chip
                v-for="(fu, fIdx) in followUps"
                :key="'fu-' + fIdx"
                small
                outlined
                color="primary"
                class="starter-chip"
                @click="sendQuick(fu)"
              >
                {{ fu }}
              </v-chip>
            </div>
          </div>
        </div>

        <div class="bid-sage-input px-3 py-2">
          <div class="d-flex align-center">
            <v-text-field
              v-model="userMessage"
              placeholder="Ask Sage about this bid..."
              outlined
              dense
              hide-details
              color="primary"
              class="mr-2"
              :disabled="isLoading"
              @keyup.enter="send"
            />
            <v-btn
              color="primary"
              dark
              icon
              :disabled="!userMessage.trim() || isLoading"
              :loading="isLoading"
              @click="send"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
          <div class="text-caption grey--text text--lighten-1 mt-1 text-center">
            Sage suggests; you decide. Scoring stays deterministic.
          </div>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import * as sageApi from "../../api/SageHRApi";
import * as log from "../../utility/logging/logger";

const SAGE_TIMEOUT_MS = 60000;

export default {
  name: "BidAuditSagePanel",
  props: {
    bidDocument: { type: Object, default: null },
    auditResult: { type: Object, default: null },
    roles: { type: Array, default: () => [] },
    isAuditing: { type: Boolean, default: false },
  },
  data() {
    return {
      collapsed: false,
      userMessage: "",
      messages: [],
      isLoading: false,
      followUps: [],
      // resolution[msgIdx_trIdx_eIdx] = "accepted" | "rejected"
      editResolutions: {},
      // per-candidate chosen role for the "Add to team" select
      swapRoleChoices: {},
      starterChips: [
        "Refine the requirements",
        "Suggest swaps for weak roles",
        "Re-rank: show weak fits first",
      ],
    };
  },
  computed: {
    roleSelectItems() {
      return this.roles.map((r) => ({
        text: r.title || "Untitled role",
        value: r.id,
      }));
    },
  },
  methods: {
    async send() {
      const text = this.userMessage.trim();
      if (!text || this.isLoading) return;
      this.userMessage = "";
      await this.dispatchTurn(text);
    },
    sendQuick(text) {
      if (this.isLoading) return;
      this.dispatchTurn(text);
    },
    async dispatchTurn(text) {
      this.messages.push({ role: "user", content: text, toolResults: null });
      this.scrollSoon();
      this.isLoading = true;
      this.followUps = [];

      const history = this.messages
        .filter((m) => m.role === "user" || m.role === "assistant")
        .slice(0, -1)
        .map((m) => ({ role: m.role, content: m.content }));

      const context = {
        mode: "react",
        wizardMode: "bid_audit",
        bidDocument: this.bidDocument,
        currentRequirements: this.buildRequirementsContext(),
        currentAuditResult: this.auditResult || null,
      };

      try {
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("SAGE_TIMEOUT")), SAGE_TIMEOUT_MS),
        );
        const result = await Promise.race([
          sageApi.SageHRChat(text, history, [], context),
          timeoutPromise,
        ]);

        if (result && result.success !== false) {
          const data = result.data || result;
          this.messages.push({
            role: "assistant",
            content: data.response || "I could not process that. Please try again.",
            toolResults: data.toolResults || data.tool_calls || [],
          });
          this.followUps = Array.isArray(data.suggestedFollowUps)
            ? data.suggestedFollowUps.slice(0, 3)
            : [];
        } else {
          this.messages.push({
            role: "assistant",
            content:
              "I'm sorry, I ran into an issue with that request. Please try again.",
            toolResults: null,
          });
        }
      } catch (err) {
        const isTimeout = err && err.message === "SAGE_TIMEOUT";
        log.logWarning(
          "Bid Sage panel turn failed: " + (err.message || err),
          "BidAuditSagePanel",
        );
        this.messages.push({
          role: "assistant",
          content: isTimeout
            ? "That took longer than expected. Try a shorter or more specific question."
            : "I'm sorry, I hit a connection issue. Please try again.",
          toolResults: null,
        });
      } finally {
        this.isLoading = false;
        this.scrollSoon();
      }
    },
    buildRequirementsContext() {
      return this.roles.map((r) => ({
        roleId: r.id,
        roleTitle: r.title,
        requirements: (r.requirements || []).map((req) => ({
          text: req.text,
          importance: req.importance,
          weight: req.weight,
        })),
      }));
    },
    // ----- refine_requirements -----
    proposedEdits(tr) {
      const result = tr.result || {};
      return result.proposedEdits || [];
    },
    editKey(msgIdx, trIdx, eIdx) {
      return msgIdx + "_" + trIdx + "_" + eIdx;
    },
    isEditResolved(msgIdx, trIdx, eIdx) {
      return !!this.editResolutions[this.editKey(msgIdx, trIdx, eIdx)];
    },
    editResolution(msgIdx, trIdx, eIdx) {
      return this.editResolutions[this.editKey(msgIdx, trIdx, eIdx)];
    },
    acceptEdit(msgIdx, trIdx, eIdx, edit) {
      // Never applies silently — only fires on this explicit Accept click.
      this.$emit("apply-edit", edit);
      this.$set(this.editResolutions, this.editKey(msgIdx, trIdx, eIdx), "accepted");
    },
    rejectEdit(msgIdx, trIdx, eIdx) {
      this.$set(this.editResolutions, this.editKey(msgIdx, trIdx, eIdx), "rejected");
    },
    anyEditAccepted(msgIdx, trIdx, tr) {
      const edits = this.proposedEdits(tr);
      for (let e = 0; e < edits.length; e++) {
        if (this.editResolution(msgIdx, trIdx, e) === "accepted") return true;
      }
      return false;
    },
    // ----- suggest_swaps -----
    swapResults(tr) {
      const result = tr.result || {};
      return result.results || [];
    },
    swapKey(msgIdx, trIdx, candIdx) {
      return msgIdx + "_" + trIdx + "_" + candIdx;
    },
    swapRoleChoice(msgIdx, trIdx, candIdx, tr) {
      const key = this.swapKey(msgIdx, trIdx, candIdx);
      if (this.swapRoleChoices[key]) return this.swapRoleChoices[key];
      // Default to the role the swap was requested for, else first role.
      const result = (tr && tr.result) || {};
      if (result.forRoleId) return result.forRoleId;
      return this.roles.length > 0 ? this.roles[0].id : "";
    },
    setSwapRoleChoice(msgIdx, trIdx, candIdx, roleId) {
      this.$set(this.swapRoleChoices, this.swapKey(msgIdx, trIdx, candIdx), roleId);
    },
    addSwapCandidate(cand, roleId) {
      this.$emit("add-swap-candidate", { candidate: cand, roleId });
    },
    // ----- re_rank_audit -----
    proposedView(tr) {
      const result = tr.result || {};
      return result.proposedView || { explanation: "" };
    },
    // ----- shared -----
    roleTitleFor(roleId) {
      const found = this.roles.find((r) => r.id === roleId);
      return found ? found.title : "Role";
    },
    importanceLabel(importance) {
      return importance === "nice_to_have" ? "Nice-to-Have" : "Must-Have";
    },
    editActionLabel(action) {
      const labels = {
        add: "Add",
        remove: "Remove",
        reweight: "Reweight",
        reimportance: "Re-importance",
      };
      return labels[action] || action;
    },
    editActionColor(action) {
      const colors = {
        add: "#10B981",
        remove: "#EF4444",
        reweight: "#8b5cf6",
        reimportance: "#F59E0B",
      };
      return colors[action] || "#007AFF";
    },
    scoreColor(score) {
      if (score >= 70) return "#10B981";
      if (score >= 40) return "#F59E0B";
      return "#EF4444";
    },
    formatMessage(content) {
      if (!content) return "";
      return content
        .replace(/\{\s*"suggestedFollowUps"[\s\S]*?\}\s*$/g, "")
        .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
        .replace(/\n/g, "<br>")
        .trim();
    },
    scrollSoon() {
      this.$nextTick(() => {
        const c = this.$refs.messageContainer;
        if (c) c.scrollTop = c.scrollHeight;
      });
    },
  },
};
</script>

<style scoped>
.bid-sage-panel {
  display: flex;
  flex-direction: column;
  border-radius: 12px;
  overflow: hidden;
}
.bid-sage-header {
  background: linear-gradient(135deg, #4c1d95 0%, #7c3aed 50%, #8b5cf6 100%);
  flex-shrink: 0;
}
.bid-sage-messages {
  flex: 1;
  overflow-y: auto;
  max-height: 520px;
  background: #f7f8fa;
}
.bid-sage-input {
  flex-shrink: 0;
  background: white;
  border-top: 1px solid #e2e8f0;
}
.bsp-row {
  margin-bottom: 12px;
}
.bsp-bubble {
  max-width: 90%;
  padding: 8px 12px;
  border-radius: 12px;
  word-break: break-word;
}
.user-row {
  display: flex;
  justify-content: flex-end;
}
.user-bubble {
  background: #007aff;
  border-bottom-right-radius: 4px;
}
.assistant-bubble {
  background: white;
  border: 1px solid #e2e8f0;
  border-bottom-left-radius: 4px;
}
.starter-chip {
  cursor: pointer;
}
.edit-row {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.rerank-card {
  background: rgba(139, 92, 246, 0.06);
  border: 1px solid rgba(139, 92, 246, 0.25);
  border-radius: 8px;
}
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}
.typing-indicator span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #8b5cf6;
  animation: typing 1.4s infinite ease-in-out;
}
.typing-indicator span:nth-child(1) {
  animation-delay: 0s;
}
.typing-indicator span:nth-child(2) {
  animation-delay: 0.2s;
}
.typing-indicator span:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.4);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
</style>

<style>
.theme--dark .bid-sage-panel .bid-sage-messages {
  background: #1a1a2e;
}
.theme--dark .bid-sage-panel .bid-sage-input {
  background: #1e1e2e;
  border-top-color: #444;
}
.theme--dark .bid-sage-panel .assistant-bubble,
.theme--dark .bid-sage-panel .edit-row {
  background: #2a2a3e;
  border-color: #444;
}
</style>
